import React, { useCallback } from 'react';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { observer } from 'mobx-react';
import { useWikiHelpTooltip } from './useWikiHelpTooltip';

export type WikiHelpIconProps = {
  tooltipId: string;
  tooltipPlacement?: TooltipProps['placement'];
  skeleton?: React.ReactNode;
  errorFallback?: React.ReactNode | ((error: unknown) => React.ReactNode);
};

function WikiHelpIcon({ tooltipId, tooltipPlacement, skeleton = null, errorFallback }: WikiHelpIconProps) {
  const theme = useTheme();
  const { status, entryState, tooltipContent, tooltipSlotProps, triggerRef, resolvedPlacement } = useWikiHelpTooltip({
    tooltipId,
    tooltipPlacement,
  });
  const attachTriggerRef = useCallback(
    (node: HTMLSpanElement | null) => {
      triggerRef.current = node;
    },
    [triggerRef],
  );

  if ((status === 'idle' || status === 'loading') && !entryState?.data) {
    return skeleton ? <>{skeleton}</> : null;
  }

  if (status === 'error' && !entryState?.data) {
    if (typeof errorFallback === 'function') {
      return <>{errorFallback(entryState?.error)}</>;
    }
    if (errorFallback) {
      return <>{errorFallback}</>;
    }
    console.log('Unable to load help content with tooltipId: ', tooltipId);
    return (
      <></>
    );
  }

  if (!tooltipContent) {
    return null;
  }

  return (
    <Tooltip title={tooltipContent} placement={resolvedPlacement} arrow disableInteractive={false} slotProps={tooltipSlotProps} leaveDelay={300}>
      <span ref={attachTriggerRef}>
        <HelpOutlineIcon style={{ color: theme.palette.gray.main }} />
      </span>
    </Tooltip>
  );
}

export default observer(WikiHelpIcon);
